import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Wallet, Users, ScrollText, Award } from "lucide-react";

const COLORS = ["#0F4C81", "#D4AF37", "#0A192F", "#64748B", "#22C55E"];

export default function AdminAnalyticsPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data));
  }, []);

  const revenueData = stats?.per_event || [];
  const paidVsPending = stats
    ? [
        { name: "Paid", value: stats.paid_registrations || 0 },
        { name: "Pending", value: Math.max(0, (stats.registrations || 0) - (stats.paid_registrations || 0)) },
      ]
    : [];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          Intelligence
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
          Analytics
        </h1>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={TrendingUp} label="Events" value={stats?.events} />
          <KPI icon={Users} label="Registrations" value={stats?.registrations} />
          <KPI icon={ScrollText} label="Position papers" value={stats?.papers} />
          <KPI icon={Award} label="Certificates" value={stats?.certificates} />
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-7 bg-white border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                Revenue by event
              </h3>
              <div className="mt-5 h-72 min-h-[280px]">
                {revenueData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="event" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip
                        contentStyle={{
                          background: "#0A192F",
                          border: "1px solid #D4AF37",
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="revenue" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    Loading chart…
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-5 bg-white border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                Payment conversion
              </h3>
              <div className="mt-5 h-72 min-h-[280px]">
                {paidVsPending?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={paidVsPending}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {paidVsPending.map((_, i) => (
                          <Cell key={i} fill={COLORS[i]} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip contentStyle={{ background: "#0A192F", border: "1px solid #D4AF37", color: "#fff" }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    Loading chart…
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6 bg-[#0A192F] text-white border-[#D4AF37]/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Lifetime revenue
                </p>
                <p className="mt-2 font-serif text-5xl text-[#D4AF37]">
                  ₹{(stats?.revenue || 0).toLocaleString()}
                </p>
                <p className="text-xs mt-2 text-slate-400">Based on paid registrations · mock payment</p>
              </div>
              <Wallet className="h-10 w-10 text-[#D4AF37]/40" />
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="p-5 rounded-xl bg-white border border-slate-200">
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        {label}
      </p>
      <p className="mt-1 font-serif text-3xl text-[#0A192F]">{value ?? "—"}</p>
    </div>
  );
}
